<template>
  <Form class="w-full" :validation-schema="validationSchema" @submit="onSubmit">
    <div class="grid gap-24">
      <template v-for="item in cartItemsWithABundleId" :key="item.Id">
        <CheckoutSelectNumberContainer
          v-if="isCartItemAFamilyConnection(item)"
          :header="`Välj nummer för familjeanslutning ${familyItemIndex(item)}`"
          :cart-item-id="
            item.BundlePlanItem && item.BundlePlanItem?.IsFamilyPlan
              ? item.BundlePlanItem.Id
              : item.Id
          "
          :bundle-id="item.BundleId"
          :field-name="
            isSecondFamilyConnectionCartItem(item)
              ? FAMILY_NUMBER_CONFIGURATION_2
              : FAMILY_NUMBER_CONFIGURATION_1
          "
          :parent-msisdn="item.BundlePlanItem?.ParentMsisdn || item.ParentMsisdn"
          :is-family-item="true"
        />

        <CheckoutSelectNumberContainer
          v-else
          :header="
            cartHasAFamilyPlanConnection
              ? 'Välj nummer för huvudabonnemang'
              : 'Välj ditt nummer'
          "
          :cart-item-id="item.BundlePlanItem?.Id ?? item.Id"
          :bundle-id="item.BundleId"
          :field-name="`${NUBMER_CONFIGURATION_FOR_}${item.BundleId}`"
          :is-family-item="false"
        />
      </template>
      <GlobalNotificationContainer
        :show="!!notifications.title"
        :is-just-text-row="true"
      />

      <p
        id="next-step-description"
        class="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <template v-if="checkoutStore.isStep2ProceedBlocked">
          Alla telefonnummer måste väljas eller verifieras innan du kan gå
          vidare.
        </template>
      </p>
      <CheckoutNextStepButton
        type="submit"
        :disabled="checkoutStore.isStep2ProceedBlocked"
        description-id="next-step-description"
      >
        Gå vidare
      </CheckoutNextStepButton>
    </div>
  </Form>
</template>

<script lang="ts" setup>
import { useValidationAggregator } from '~/formAggregator';
import type { SetNumberOperatorPlansPayload } from '~/models/shopping_cart/SetNumberOperatorRequest.api.model';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';
import type { HalebopRequestErrorData } from '~/repository/factory';
const { $api } = useNuxtApp();
const notifications = useNotificationStore();
const checkoutStore = useMyCheckoutStore();
const { validateAll } = useValidationAggregator();
const { $validator } = useNuxtApp();
const { pushNumberOptionEvent } = useGoogleAnalyticsEvents();

onMounted(() => {
  checkoutStore.ui.bindingPeriodCheckHasActiveBinding = null;
});

const FAMILY_NUMBER_CONFIGURATION_1 = 'family_number_configuration_1';
const FAMILY_NUMBER_CONFIGURATION_2 = 'family_number_configuration_2';
const NUBMER_CONFIGURATION_FOR_ = 'number_configuration_for_';

const cartItemsWithABundleId = computed(() =>
  checkoutStore.fetchedData.cart.cartItems.filter(
    (item) => item.BundleId != undefined,
  ),
);

const isCartItemAFamilyConnection = (
  cartItem: ShoppingCartItemModel,
): boolean => {
  const isFamily =
    (cartItem.BundlePlanItem && cartItem.BundlePlanItem?.IsFamilyPlan) ||
    cartItem.IsFamilyPlan
      ? true
      : false;
  return isFamily;
};

const isSecondFamilyConnectionCartItem = (
  cartItem: ShoppingCartItemModel,
): boolean => {
  const index = checkoutStore.fetchedData.cart.cartItems.findIndex(
    (el) => el.Id === cartItem.Id,
  );

  const cartItemsRenderedBeforeThisItem =
    checkoutStore.fetchedData.cart.cartItems.slice(0, index);

  return !!cartItemsRenderedBeforeThisItem.find((previousItem) =>
    previousItem.BundlePlanItem && previousItem.BundlePlanItem?.IsFamilyPlan
      ? previousItem.BundlePlanItem?.IsFamilyPlan === true
      : previousItem.IsFamilyPlan === true,
  );
};

const familyItemIndex = (cartItem: ShoppingCartItemModel): number => {
  const familyItems = cartItemsWithABundleId.value.filter((item) =>
    isCartItemAFamilyConnection(item),
  );
  return familyItems.findIndex((item) => item.Id === cartItem.Id) + 1;
};

const cartHasAFamilyPlanConnection = computed(
  () =>
    !!checkoutStore.fetchedData.cart.cartItems.find((item) =>
      item.BundlePlanItem && item.BundlePlanItem?.IsFamilyPlan
        ? item.BundlePlanItem?.IsFamilyPlan === true
        : item.IsFamilyPlan === true,
    ),
);

const validationSchema = computed(() => {
  return cartItemsWithABundleId.value.reduce<{
    [key: string]: (value: string) => string | boolean;
  }>((acc, item) => {
    let fieldName: string;
    if (isSecondFamilyConnectionCartItem(item)) {
      fieldName = FAMILY_NUMBER_CONFIGURATION_2;
    } else if (isCartItemAFamilyConnection(item)) {
      fieldName = FAMILY_NUMBER_CONFIGURATION_1;
    } else {
      fieldName = `${NUBMER_CONFIGURATION_FOR_}${item.BundleId}`;
    }
    acc[fieldName] = $validator.numberOptionStep2IsSelected;
    return acc;
  }, {});
});

const onSubmit = async () => {
  try {
    pushNumberOptionEvent(checkoutStore.trackingData.numberOptions);
  } catch (e) {
    console.error(e);
  }

  try {
    checkoutStore.ui.numberConfigFormIsSubmitting = true;
    const results = await validateAll();

    const errorResult = results.filter((result) => result.valid == false);
    if (errorResult.length > 0) {
      const firstErrorObject = errorResult[0].errors;

      const element = document.getElementById(Object.keys(firstErrorObject)[0]);
      if (element) {
        element.focus();
      }
    }

    if (results.some((r) => !r.valid)) {
      return;
    }

    notifications.reset();
    checkoutStore.goToNextStep();

    try {
      const mainPlans = checkoutStore.confirmOrderRequestPayload.MainPlans;
      const setNumberOperatorPayload: SetNumberOperatorPlansPayload = {
        MainPlans: mainPlans,
      };

      const setOperatorResponse =
        await $api.ShoppingCartModule.SetNumberOperatorAsync(
          setNumberOperatorPayload,
        );

      if (setOperatorResponse.UpdateShoppingCart === true) {
        checkoutStore.refetchCartForUpdatedDiscounts();
      }
    } catch (e) {
      // Do nothing
    }
  } catch (error) {
    if (isNuxtError<HalebopRequestErrorData>(error) && error.message) {
      notifications.setErrorNotificationFromCatchClause(
        error.message,
        'Kunde inte gå vidare.',
      );

      logError(error);
      return;
    }

    notifications.setErrorNotificationFromCatchClause();
  } finally {
    checkoutStore.ui.numberConfigFormIsSubmitting = false;
  }
};
</script>

<style lang="scss" scoped></style>
